// Live solvers: models fast enough to re-run while you drag. hemo-rt (ai lane, sims/hemo-rt) is a
// linear pressure-inlet distributed 1D model of the Circle of Willis in wasm. v0.2 cuts a vessel
// with a sealed clot anywhere along it, keeping both compliant stubs and every collateral path, and
// solves a settled cardiac cycle in about 2 ms. The app shows clot controls only when a solver
// loads; nothing here stands in for one.
//
// Solving is async (a Promise) so it can move into a Web Worker without touching the controller.
// Until it does, only a graph the size of the Circle of Willis may solve on the main thread.

import { colormap } from '../../ui/scales.ts';
import { STAND_IN } from './discover.ts';
import { SimFiles, type SimSource } from './files.ts';
import { robustRange, type Overlay } from './overlay.ts';
import { frameTimes, type TimeAxis } from './time.ts';

/** The most vessels allowed to solve on the main thread: the 22-edge CoW (1.7 ms in Chrome). An 88-edge
 *  graph took 260 ms in wasm (ai, 2026-10-01); anything bigger waits for a worker, and the 729-segment HD
 *  network never solves live: it shows hemo's precomputed exports. */
export const MAIN_THREAD_MAX_VESSELS = 22;

/** mmHg → Pa. */
const MMHG = 133.322387415;
/** Blood density of the CoW model, kg/m³ (pipelines/sim/cow/distributed.py: Alastruey 2007 §2.1.1). Its
 *  inertance per metre is ρ/A, so the reference area a vessel was built with is ρ/L′. */
export const DENSITY = 1050;

/** A vessel of the solver's network, with the reference geometry its coefficients imply. */
export interface LiveVessel {
  id: string;
  name: string;
  lengthMm: number;
  /** Reference radius (mm) and area (m²), from the model's own inertance: A₀ = ρ/L′. */
  radiusMm: number;
  area: number;
  /** Wall compliance per metre, dA/dp in m²/Pa. */
  compliance: number;
}

/** A sealed clot: the vessel (its original segment id) and where along it, 0.1–0.9 from its start. */
export interface LiveClot {
  vessel: string;
  s: number;
}

export interface LiveRequest {
  clots: LiveClot[];
  /** Vessels taken out of the network (collaterals off), on top of the request's own. */
  absent?: string[];
}

export interface LivePart {
  side: 'whole' | 'proximal' | 'distal';
  sStart: number;
  sEnd: number;
  /** Absolute columns in the flat arrays. */
  offset: number;
  count: number;
}

export interface LiveSegment {
  id: string;
  state: 'open' | 'absent' | 'whole_occluded' | 'axial_clot';
  /** Samples along the original segment: absolute columns and their fractions (a clot's appears twice). */
  offset: number;
  count: number;
  s: Float64Array;
  parts: LivePart[];
  /** The clot and its two face columns, proximal first. */
  clot: { s: number; proximal: number; distal: number } | null;
}

export interface LiveResult {
  /** What was solved: the clots after checking, and every absent vessel. */
  clots: LiveClot[];
  absent: string[];
  /** One cardiac cycle, periodic. */
  axis: TimeAxis;
  /** In network order. */
  segments: LiveSegment[];
  /** Columns of the flat arrays. */
  samples: number;
  /** `[phase][sample]`: flow positive from the segment's start to its end (mL/s), pressure (mmHg). */
  flow: Float32Array;
  pressure: Float32Array;
  /** Wall time from request to parsed result, ms. */
  computeMs: number;
}

export interface LiveSolver {
  id: string;
  /** Where it loaded from, under sims/. */
  path: string;
  model: string;
  provenance: string;
  space: string;
  /** The request's own sources (parameter sets and inputs); `synthetic` when the name says so. */
  sources: { id: string; synthetic: boolean }[];
  units: { flow: string; pressure: string; velocity: string; radius: string };
  vessels: LiveVessel[];
  /** The intact network, solved at load. */
  baseline: LiveResult;
  /** The build's own checks against its golden cases, when it ships them. */
  validation: { cases: number; maxFlowError: number; maxPressureError: number } | null;
  solve(req: LiveRequest): Promise<LiveResult>;
}

/** The one call of the wasm module the app needs. It throws its error JSON on bad input. */
export interface HemoRtApi {
  solve_json(input: string): string;
}

export class LiveSolveError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

// brain-atlas-hemo-request-v0.1, as far as the app reads it; everything else passes through.
export interface HemoRtVessel {
  id: string;
  start_node: string;
  end_node: string;
  length_mm: number;
  resistance_per_m: number;
  inertance_per_m: number;
  compliance_per_m: number;
  [k: string]: unknown;
}

export interface HemoRtRequest {
  schema: string;
  graph: { source_node_id: string; node_ids: string[]; vessels: HemoRtVessel[]; [k: string]: unknown };
  absent_vessel_ids?: string[];
  occluded_vessel_ids?: string[];
  sources?: string[];
  [k: string]: unknown;
}

interface AxialSegmentRaw {
  vessel_id: string;
  state: LiveSegment['state'];
  offset: number;
  count: number;
  s: number[];
  parts: { side: LivePart['side']; s_start: number; s_end: number; offset: number; count: number }[];
  clot?: { s: number; proximal_face: number; distal_face: number };
}

interface HemoRtResponse {
  model?: string;
  provenance?: string;
  space?: string;
  units?: Record<string, string>;
  time_s: number[];
  period_s: number;
  axial_samples?: { schema?: string; segments: AxialSegmentRaw[]; flow_ml_s: number[][]; pressure_mmhg: number[][] };
}

interface HemoRtArtifacts {
  native_validation?: { legacy_golden_cases?: number; axial_golden_cases?: number; max_axial_flow_error_ml_s?: number; max_axial_pressure_error_mmhg?: number };
}

const ABBR: Record<string, string> = { ica: 'ICA', mca: 'MCA', aca: 'ACA', pca: 'PCA', acom: 'ACom', pcom: 'PCom', sca: 'SCA', aica: 'AICA', pica: 'PICA' };

/**
 * 'ba:left-mca-m1' → 'Left MCA M1'. 'ba:left-mca-distal-model' → 'Left MCA distal (aggregate)': the CoW model
 * splits the MCA into an assumed 15 mm M1 and the 104 mm aggregate beyond it (pipelines/sim/cow/README.md).
 */
export function vesselName(id: string): string {
  const words = id
    .replace(/^[a-z]+:/, '')
    .split('-')
    .filter(Boolean)
    .map((w) => ABBR[w] ?? (/^[mapv]\d$/.test(w) ? w.toUpperCase() : w));
  const s = words.join(' ').replace(/ distal model$/, ' distal (aggregate)').replace(/ model$/, ' (model)');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The CoW's collaterals: the ACom and the PComs (the switch that takes them out of the network). */
export function collateralVessels(vessels: { id: string }[]): string[] {
  return vessels.filter((v) => /(^|:)acom$|-pcom$/.test(v.id)).map((v) => v.id);
}

/**
 * Vessels the packs have no geometry for, per the data-pack lane's sim crosswalk (pipelines/packs/README.md):
 * the extracranial ICA lies outside the atlas, and the distal MCA is an aggregate.
 */
const NO_GEOMETRY = /-(ica-extracranial|mca-distal-model)$/;

/**
 * The pack structure a solver vessel colours: its own id, else `<id>-artery` (ba:basilar →
 * ba:basilar-artery), else the longest hyphen-bounded prefix the pack has (ba:left-ica-terminal, the
 * distal end of the ICA → ba:left-ica). Null when the pack draws nothing for it.
 */
export function vesselStructure(id: string, has: (id: string) => boolean): string | null {
  if (NO_GEOMETRY.test(id)) return null;
  if (has(id)) return id;
  if (has(`${id}-artery`)) return `${id}-artery`;
  const parts = id.split('-');
  for (let n = parts.length - 1; n >= 2; n--) {
    const p = parts.slice(0, n).join('-');
    if (has(p)) return p;
  }
  return null;
}

/** Structure → its vessels, in network order (the left ACA's A1 and A2). */
export function structureVessels(vessels: { id: string }[], has: (id: string) => boolean): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const v of vessels) {
    const s = vesselStructure(v.id, has);
    if (s) out.set(s, [...(out.get(s) ?? []), v.id]);
  }
  return out;
}

/** A request vessel's reference geometry: A₀ = ρ/L′ (the model's inertance), C′ as given. */
export function vesselGeometry(v: HemoRtVessel): LiveVessel {
  const area = DENSITY / v.inertance_per_m;
  return { id: v.id, name: vesselName(v.id), lengthMm: v.length_mm, area, radiusMm: Math.sqrt(area / Math.PI) * 1000, compliance: v.compliance_per_m };
}

/** Parse a v0.2 response's flattened axial samples. */
export function parseHemoRt(res: HemoRtResponse, clots: LiveClot[], absent: string[], computeMs: number): LiveResult {
  const ax = res.axial_samples;
  if (!ax) throw new Error('hemo-rt: the response has no axial_samples (needs runtime v0.2)');
  const P = res.time_s.length;
  if (!P || ax.flow_ml_s.length !== P || ax.pressure_mmhg.length !== P) throw new Error('hemo-rt: axial sample phases don’t match time_s');
  const S = ax.flow_ml_s[0]!.length;
  const period = res.period_s;
  const uniform = res.time_s.every((t, i) => Math.abs(t - res.time_s[0]! - (i * period) / P) < 1e-9);
  const axis: TimeAxis = uniform
    ? { t0: res.time_s[0]!, dt: period / P, frames: P, times: null, periodic: true, period, interp: 'linear' }
    : { t0: res.time_s[0]!, dt: NaN, frames: P, times: Float64Array.from(res.time_s), periodic: true, period, interp: 'linear' };
  const flow = new Float32Array(P * S);
  const pressure = new Float32Array(P * S);
  for (let p = 0; p < P; p++) {
    const fr = ax.flow_ml_s[p]!;
    const pr = ax.pressure_mmhg[p]!;
    if (fr.length !== S || pr.length !== S) throw new Error('hemo-rt: ragged axial samples');
    flow.set(fr, p * S);
    pressure.set(pr, p * S);
  }
  const segments: LiveSegment[] = ax.segments.map((g) => {
    if (g.offset < 0 || g.offset + g.count > S || g.s.length !== g.count) throw new Error(`hemo-rt: segment ${g.vessel_id} samples out of range`);
    return {
      id: g.vessel_id,
      state: g.state,
      offset: g.offset,
      count: g.count,
      s: Float64Array.from(g.s),
      parts: g.parts.map((pt) => ({ side: pt.side, sStart: pt.s_start, sEnd: pt.s_end, offset: pt.offset, count: pt.count })),
      clot: g.clot ? { s: g.clot.s, proximal: g.clot.proximal_face, distal: g.clot.distal_face } : null,
    };
  });
  return { clots, absent, axis, segments, samples: S, flow, pressure, computeMs };
}

function solveError(err: unknown): Error {
  const text = err instanceof Error ? err.message : String(err);
  try {
    const e = JSON.parse(text) as { code?: string; message?: string };
    if (e && typeof e.message === 'string') return new LiveSolveError(e.code ?? 'error', e.message);
  } catch {
    // Not the solver's JSON: pass the error on as it is.
  }
  return err instanceof Error ? err : new Error(text);
}

const clampS = (s: number) => Math.min(0.9, Math.max(0.1, Number.isFinite(s) ? s : 0.5));

/**
 * A LiveSolver over the hemo-rt module and its baseline request; solves the baseline before
 * returning. Refuses a graph too big for the main thread.
 */
export function hemoRtSolver(api: HemoRtApi, request: HemoRtRequest, opts: { path: string; artifacts?: HemoRtArtifacts | null; now?: () => number } = { path: '' }): LiveSolver {
  if (request.graph.vessels.length > MAIN_THREAD_MAX_VESSELS)
    throw new LiveSolveError('needs_worker', `${request.graph.vessels.length} vessels is more than the ${MAIN_THREAD_MAX_VESSELS} that may solve on the main thread`);
  const now = opts.now ?? (() => performance.now());
  const own = new Set(request.absent_vessel_ids ?? []);
  const vessels = request.graph.vessels.map(vesselGeometry);
  const known = new Set(vessels.map((v) => v.id));
  let meta: Pick<HemoRtResponse, 'model' | 'provenance' | 'space' | 'units'> = {};
  const run = (req: LiveRequest): LiveResult => {
    const absent = [...new Set([...own, ...(req.absent ?? [])])].filter((id) => known.has(id));
    const out = new Set(absent);
    // One clot per vessel (the last one asked for), only on vessels still in the network.
    const byVessel = new Map<string, LiveClot>();
    for (const c of req.clots) if (known.has(c.vessel) && !out.has(c.vessel)) byVessel.set(c.vessel, { vessel: c.vessel, s: clampS(c.s) });
    const clots = [...byVessel.values()];
    const t0 = now();
    let text: string;
    try {
      text = api.solve_json(
        JSON.stringify({
          ...request,
          absent_vessel_ids: absent,
          clots: clots.map((c) => ({ vessel_id: c.vessel, s: c.s })),
          include_axial_samples: true,
          include_internal_pressures: false,
        }),
      );
    } catch (err) {
      throw solveError(err);
    }
    const res = JSON.parse(text) as HemoRtResponse;
    meta = { model: res.model, provenance: res.provenance, space: res.space, units: res.units };
    const parsed = parseHemoRt(res, clots, absent, 0);
    parsed.computeMs = now() - t0;
    return parsed;
  };
  const baseline = run({ clots: [] });
  const nv = opts.artifacts?.native_validation;
  const cases = (nv?.legacy_golden_cases ?? 0) + (nv?.axial_golden_cases ?? 0);
  return {
    id: 'hemo-rt',
    path: opts.path,
    model: meta.model ?? 'hemo-rt',
    provenance: meta.provenance ?? 'not stated',
    space: meta.space ?? 'not stated',
    sources: (request.sources ?? []).map((id) => ({ id, synthetic: STAND_IN.test(id) })),
    units: { flow: meta.units?.flow ?? 'mL/s', pressure: meta.units?.pressure ?? 'mmHg', velocity: 'cm/s', radius: 'mm' },
    vessels,
    baseline,
    validation: cases ? { cases, maxFlowError: nv?.max_axial_flow_error_ml_s ?? NaN, maxPressureError: nv?.max_axial_pressure_error_mmhg ?? NaN } : null,
    solve: async (req) => run(req),
  };
}

type HemoRtModule = HemoRtApi & { default: (init: { module_or_path: string }) => Promise<unknown> };

/**
 * hemo-rt from `sims/<dir>/`: its baseline request, and the wasm-bindgen module under web/. Null
 * when the run isn't there, or its graph is too big to solve on the main thread; a module that fails
 * to load or solve throws.
 */
export async function loadHemoRt(src: SimSource, dir = 'hemo/hemo-rt-axial-v0.2', importModule: (url: string) => Promise<unknown> = (url) => import(/* @vite-ignore */ url)): Promise<LiveSolver | null> {
  const files = new SimFiles(src, dir);
  const request = await files.maybeJson<HemoRtRequest>('baseline-request.json');
  if (!request?.graph?.vessels?.length) return null;
  if (request.graph.vessels.length > MAIN_THREAD_MAX_VESSELS) {
    console.info(`[live] ${dir}: ${request.graph.vessels.length} vessels; only ${MAIN_THREAD_MAX_VESSELS} solve on the main thread, so it isn't live here`);
    return null;
  }
  const artifacts = await files.maybeJson<HemoRtArtifacts>('artifacts.json').catch(() => null);
  const mod = (await importModule(files.url('web/ba_hemo_rt.js'))) as HemoRtModule;
  await mod.default({ module_or_path: files.url('web/ba_hemo_rt_bg.wasm') });
  return hemoRtSolver(mod, request, { path: dir, artifacts });
}

export type Side = 'proximal' | 'distal';

/** Where fraction `at` falls on a segment: the part holding it and the two samples around it. At a
 *  clot's own fraction, `side` picks the face; samples are never blended across the clot. */
export function locate(seg: LiveSegment, at: number, side: Side = 'distal'): { part: LivePart; i0: number; i1: number; w: number } | null {
  if (!seg.count || !seg.parts.length) return null;
  const x = Math.min(1, Math.max(0, at));
  const holding = seg.parts.filter((p) => x >= p.sStart - 1e-12 && x <= p.sEnd + 1e-12);
  const part = holding.length > 1 ? (holding.find((p) => p.side === side) ?? holding[0]!) : holding[0];
  if (!part) return null;
  const local = seg.s.subarray(part.offset - seg.offset, part.offset - seg.offset + part.count);
  for (let k = 1; k < local.length; k++)
    if (x <= local[k]! + 1e-12) {
      const span = local[k]! - local[k - 1]!;
      return { part, i0: part.offset + k - 1, i1: part.offset + k, w: span > 0 ? Math.min(1, Math.max(0, (x - local[k - 1]!) / span)) : 0 };
    }
  return { part, i0: part.offset + part.count - 1, i1: part.offset + part.count - 1, w: 0 };
}

/** Flow (mL/s), pressure (mmHg), velocity (cm/s) and radius (mm) over the cycle at one point. */
export interface PointSeries {
  flow: Float32Array;
  pressure: Float32Array;
  velocity: Float32Array;
  radius: Float32Array;
}

/**
 * A vessel's series over the cycle at fraction `at` of its original length (at a clot, `side` picks
 * its face). Velocity is Q/A₀, the CoW model's own definition; radius is A₀ with the model's linear
 * wall change C′(p − p̄) about the point's mean pressure. Null for a vessel taken out of the network.
 */
export function seriesAt(solver: Pick<LiveSolver, 'vessels'>, r: LiveResult, vessel: string, at = 0.5, side: Side = 'distal'): PointSeries | null {
  const seg = r.segments.find((g) => g.id === vessel);
  const v = solver.vessels.find((x) => x.id === vessel);
  const loc = seg && locate(seg, at, side);
  if (!seg || !v || !loc) return null;
  const P = r.axis.frames;
  const S = r.samples;
  const flow = new Float32Array(P);
  const pressure = new Float32Array(P);
  const velocity = new Float32Array(P);
  const radius = new Float32Array(P);
  let mean = 0;
  for (let p = 0; p < P; p++) {
    const q = r.flow[p * S + loc.i0]! * (1 - loc.w) + r.flow[p * S + loc.i1]! * loc.w;
    const pr = r.pressure[p * S + loc.i0]! * (1 - loc.w) + r.pressure[p * S + loc.i1]! * loc.w;
    flow[p] = q;
    pressure[p] = pr;
    velocity[p] = ((q * 1e-6) / v.area) * 100;
    mean += pr / P;
  }
  for (let p = 0; p < P; p++) radius[p] = Math.sqrt(Math.max(0, v.area + v.compliance * (pressure[p]! - mean) * MMHG) / Math.PI) * 1000;
  return { flow, pressure, velocity, radius };
}

/** Cycle mean of a series. */
export function cycleMean(x: ArrayLike<number>): number {
  let sum = 0;
  for (let i = 0; i < x.length; i++) sum += x[i]!;
  return x.length ? sum / x.length : NaN;
}

/** A vessel's mean flow over the cycle at mid-length; NaN when it's out of the network. */
export function meanFlow(solver: Pick<LiveSolver, 'vessels'>, r: LiveResult, id: string): number {
  const s = seriesAt(solver, r, id);
  return s ? cycleMean(s.flow) : NaN;
}

export interface FlowChange {
  id: string;
  name: string;
  /** Mean flow magnitude over the cycle, before and after (mL/s); below 1 µL/s reads as 0. */
  before: number;
  after: number;
  /** The mean flow changed direction (an ACom feeding the other side, a reversed A1). */
  reversed: boolean;
}

/**
 * The open vessels whose mean flow grew most (the collaterals taking over) and shrank most (what
 * the clot starves), against the baseline. Magnitudes, with direction changes flagged.
 */
export function flowChanges(solver: Pick<LiveSolver, 'vessels' | 'baseline'>, r: LiveResult, limit = 3, minMlS = 0.01): { gained: FlowChange[]; lost: FlowChange[] } {
  const cut = new Set([...r.clots.map((c) => c.vessel), ...r.absent]);
  const clean = (v: number) => (Math.abs(v) < 1e-3 ? 0 : v);
  const all = solver.vessels
    .filter((v) => !cut.has(v.id))
    .map((v) => {
      const b = clean(meanFlow(solver, solver.baseline, v.id));
      const a = clean(meanFlow(solver, r, v.id));
      return { id: v.id, name: v.name, before: Math.abs(b), after: Math.abs(a), reversed: Math.abs(b) >= minMlS && Math.abs(a) >= minMlS && Math.sign(a) !== Math.sign(b) };
    })
    .filter((c) => Number.isFinite(c.before) && Number.isFinite(c.after));
  const by = (d: (c: FlowChange) => number) => all.filter((c) => d(c) >= minMlS).sort((x, y) => d(y) - d(x)).slice(0, limit);
  return { gained: by((c) => c.after - c.before), lost: by((c) => c.before - c.after) };
}

export type LiveQuantity = 'velocity' | 'pressure' | 'flow';

/**
 * Live flow on the pack's arteries: per structure, the length-weighted mean over its vessels of
 * their samples' speed (or pressure, or flow magnitude), phase by phase; or, with `mean`, one frame
 * of the cycle mean, for a scene whose clock isn't the cardiac cycle. The range comes from the
 * baseline, so a clot darkens what it starves against the same scale. A structure whose vessels are
 * all out of the network keeps its base colour (NaN).
 */
export function liveOverlay(
  solver: Pick<LiveSolver, 'vessels' | 'baseline' | 'units'>,
  r: LiveResult,
  has: (id: string) => boolean,
  opts: { quantity?: LiveQuantity; mean?: boolean } = {},
): Overlay | null {
  const quantity = opts.quantity ?? 'velocity';
  const groups = structureVessels(solver.vessels, has);
  const keys = [...groups.keys()];
  if (!keys.length) return null;
  const byId = new Map(solver.vessels.map((v) => [v.id, v]));
  const magnitude = quantity !== 'pressure';
  const series = (res: LiveResult) => {
    const P = res.axis.frames;
    const S = res.samples;
    const segs = new Map(res.segments.map((g) => [g.id, g]));
    const values = new Float32Array(P * keys.length);
    keys.forEach((key, k) => {
      const members = groups.get(key)!.map((id) => [segs.get(id), byId.get(id)!] as const).filter((m): m is [LiveSegment, LiveVessel] => !!m[0] && m[0].count > 0);
      const total = members.reduce((a, [, v]) => a + v.lengthMm, 0);
      for (let p = 0; p < P; p++) {
        if (!total) {
          values[p * keys.length + k] = NaN;
          continue;
        }
        let sum = 0;
        for (const [g, v] of members) {
          let acc = 0;
          for (let c = g.offset; c < g.offset + g.count; c++) {
            const x = quantity === 'pressure' ? res.pressure[p * S + c]! : res.flow[p * S + c]!;
            acc += quantity === 'velocity' ? (Math.abs(x) * 1e-4) / v.area : magnitude ? Math.abs(x) : x;
          }
          sum += (acc / g.count) * v.lengthMm;
        }
        values[p * keys.length + k] = sum / total;
      }
    });
    return values;
  };
  const meanOf = (vals: Float32Array, P: number) => {
    const out = new Float32Array(keys.length);
    for (let k = 0; k < keys.length; k++) {
      let sum = 0;
      for (let p = 0; p < P; p++) sum += vals[p * keys.length + k]!;
      out[k] = sum / P;
    }
    return out;
  };
  const values = series(r);
  const base = series(solver.baseline);
  const units = quantity === 'velocity' ? solver.units.velocity : quantity === 'pressure' ? solver.units.pressure : solver.units.flow;
  const ramp = quantity === 'pressure' ? 'pressure' : 'velocity';
  const common = { id: 'live:vessels', quantity, units, ramp, keys, colormap: colormap(ramp) } as const;
  if (opts.mean) {
    const still: TimeAxis = { t0: 0, dt: NaN, frames: 1, times: Float64Array.of(0), periodic: false, period: 0, interp: 'previous' };
    return { ...common, times: [0], values: meanOf(values, r.axis.frames), axis: still, range: robustRange(meanOf(base, solver.baseline.axis.frames), magnitude) };
  }
  return { ...common, times: Array.from(frameTimes(r.axis)), values, axis: r.axis, range: robustRange(base, magnitude) };
}
